import React, { useEffect, useMemo, useState } from "react";
import { Calendar, Check, Pencil } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

/**
 * Staff records the start date for a student's course. The end date is auto-
 * computed by the backend from the course's `duration_days`. We mirror the
 * arithmetic on the client so the staff member sees the projected end date as
 * they pick a start date.
 */
export default function CourseSchedulePanel({ registration, onSaved }) {
  const [editing, setEditing] = useState(!registration.course_start_date);
  const [startDate, setStartDate] = useState(registration.course_start_date || "");
  const [duration, setDuration] = useState(null);
  const [saving, setSaving] = useState(false);

  // Pull the course duration so we can preview the end date live while editing
  useEffect(() => {
    if (!registration.course_id) return;
    api.get("/courses", { params: { include_archived: true } })
      .then((r) => {
        const c = (r.data || []).find((x) => x.id === registration.course_id);
        if (c) setDuration(c.duration_days);
      });
  }, [registration.course_id]);

  const projectedEnd = useMemo(() => {
    if (!startDate || !duration) return null;
    const d = new Date(`${startDate}T00:00:00`);
    if (Number.isNaN(d.getTime())) return null;
    d.setDate(d.getDate() + Number(duration));
    return d.toISOString().slice(0, 10);
  }, [startDate, duration]);

  const save = async (e) => {
    e.preventDefault();
    if (!startDate) {
      toast.error("Pick a start date");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/registrations/${registration.id}/course-schedule`, {
        course_start_date: startDate,
      });
      toast.success(`Course schedule set · ends ${data.course_end_date}`);
      setEditing(false);
      onSaved?.(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not save schedule");
    } finally {
      setSaving(false);
    }
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
        day: "2-digit", month: "short", year: "numeric",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="border border-slate-200 p-5" data-testid="course-schedule-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar size={18} weight="duotone" className="text-[var(--sms-primary)]" />
          <div className="font-mono-sms text-xs uppercase tracking-[0.2em]">Course schedule</div>
        </div>
        {!editing && registration.course_start_date && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid="btn-edit-schedule"
            className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-[var(--sms-primary)] inline-flex items-center gap-1"
          >
            <Pencil size={12} weight="bold" /> Edit
          </button>
        )}
      </div>

      {!editing ? (
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">Started on</div>
            <div className="font-semibold text-base mt-1" data-testid="schedule-start-display">
              {fmt(registration.course_start_date)}
            </div>
          </div>
          <div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">Ends on (auto)</div>
            <div className="font-semibold text-base mt-1" data-testid="schedule-end-display">
              {fmt(registration.course_end_date)}
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={save} className="space-y-3" data-testid="schedule-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Start date</div>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="input-course-start-date"
                className="sms-input"
                required
              />
            </div>
            <div>
              <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">
                End date · auto (+{duration ?? "?"}d)
              </div>
              <div className="sms-input bg-slate-50 text-slate-600" data-testid="preview-course-end-date">
                {projectedEnd ? fmt(projectedEnd) : "—"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button
              type="submit"
              disabled={saving || !startDate}
              data-testid="btn-save-schedule"
              className="sms-btn-primary inline-flex items-center gap-2"
            >
              <Check size={14} weight="bold" />
              {saving ? "Saving…" : "Save schedule"}
            </button>
            {registration.course_start_date && (
              <button
                type="button"
                onClick={() => { setEditing(false); setStartDate(registration.course_start_date); }}
                data-testid="btn-cancel-schedule"
                className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
